import { compact, first, isEmpty, isNull, isUndefined, noop } from 'lodash';
import {
  createContext,
  Dispatch,
  MutableRefObject,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePrevious } from 'react-use';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import { PAGE_KIND_TO_EDUCATION_SLIDE_ID } from '@/modules/education/Education.constants';
import { useEducationSlideOverrides } from '@/modules/education/EducationSlideOverrides/EducationSlideOverrides.context';
import { isCustomSlideIdentifier } from '@/modules/education/EducationSlideTrowser/utils/slideIdentifier.utils';
import { PresentationBundleKind } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

import {
  useClientPresentationDesignerContext,
  usePresentationConfiguration,
} from '../ClientPresentationDesigner.context';
import { isLegacySinglePageDiagramBundle } from '../ClientPresentationDesigner.utils';
import {
  BUNDLE_TO_DISPLAY_NAME,
  isSinglePageBundle,
} from '../components/ClientPresentationDesignerTreeView/ClientPresentationDesignerTreeView.constants';
import { ClientPresentationV2Bundle } from '../types/ClientPresentation.PresentationBundleType';

export interface SlideInfo {
  /**
   * The bundle ID attached to the slide
   */
  bundleId: string;
  /**
   * The page ID attached to the slide
   */
  pageId?: string | null | undefined;
  /**
   * The index of the slide in the bundle+page combo, if there are 2+ slides in the bundle+page combo
   */
  pageIndex: number;
  /**
   * If reordering bundles, this is the new index of the bundle
   */
  newBundleIndex?: number;
  /**
   * If reordering pages within a bundle, this is the new index of the page
   */
  newPageIndex?: number;
}

export interface SlideDetails extends SlideInfo {
  title: string;
  slideId: string;
  includeInToC: boolean;
  bundleKind: PresentationBundleKind;
  /** Per-bundle subtitle line rendered under the title in the table of
   *  contents. Distinct from the static
   *  `getTableOfContentsBundleSubtitle(bundleKind)` map, which can only
   *  vary by kind — this slot carries data sourced from the bundle's
   *  configuration (e.g. the `deathOrderDisplay` on an asset-
   *  distribution bundle). When null, the static map's value (if any)
   *  is used instead. */
  subtitle?: string | null;
}

export interface ClientPresentationDesignerPaginationContextShape {
  slideIdList: string[];
  setSlideIdList: Dispatch<SetStateAction<string[]>>;
  bundles: ClientPresentationV2Bundle[];
  hiddenBundleIds: ReadonlySet<string>;
  /**
   * Per-slideId registration count. Multiple components can register
   * the same slideId (e.g. `BalanceSheetSlide` registers at the parent
   * AND at the inner page-0 child); the cleanup in `useRegisterSlide`
   * uses this ref to only drop a slide from `slideIdList` once *all*
   * consumers have unmounted, so a child unmount can't pull a slideId
   * the parent still depends on.
   */
  slideIdRegistrationCounts: MutableRefObject<Map<string, number>>;
}

const ClientPresentationDesignerPaginationContext =
  createContext<ClientPresentationDesignerPaginationContextShape>({
    slideIdList: [],
    setSlideIdList: noop,
    bundles: [],
    hiddenBundleIds: new Set(),
    slideIdRegistrationCounts: { current: new Map() },
  });

function useClientPresentationDesignerPaginationContext() {
  return useGuardedContext(
    ClientPresentationDesignerPaginationContext,
    'ClientPresentationDesignerPaginationContext'
  );
}

export function usePresentationSlideIds(): string[] {
  const { slideIdList } = useClientPresentationDesignerPaginationContext();
  return slideIdList;
}

export interface ClientPresentationDesignerPaginationProviderProps
  extends PropsWithChildren {
  /**
   * A list of bundles on the current presentation; this can be provided by the print view's
   * load function or the editor's form
   */
  bundles: ClientPresentationV2Bundle[];
}

type EducationSlideVisibility = Pick<
  ReturnType<typeof useEducationSlideOverrides>,
  'customSlidesMap' | 'isSlideHidden'
>;

export function isPresentationBundleHidden(
  bundle: ClientPresentationV2Bundle,
  { customSlidesMap, isSlideHidden }: EducationSlideVisibility
): boolean {
  const page = bundle.pages[0];
  if (!page) return false;

  if (bundle.type === PresentationBundleKind.EducationPageBundle) {
    const educationSlideId = PAGE_KIND_TO_EDUCATION_SLIDE_ID[page.type];
    return Boolean(educationSlideId && isSlideHidden(educationSlideId));
  }

  const pageContent = page.customPage?.pageContent;
  if (
    bundle.type !== PresentationBundleKind.CustomPageBundle ||
    !pageContent ||
    !isCustomSlideIdentifier(pageContent)
  ) {
    return false;
  }

  const customSlide = customSlidesMap[pageContent];
  return !customSlide || customSlide.isHidden;
}

export function ClientPresentationDesignerPaginationProvider({
  children,
  bundles,
}: ClientPresentationDesignerPaginationProviderProps) {
  const [slideIdList, setSlideIdList] = useState<string[]>([]);
  const slideIdRegistrationCounts = useRef<Map<string, number>>(new Map());
  const educationSlideVisibility = useEducationSlideOverrides();
  const hiddenBundleIds = useMemo(
    () =>
      new Set(
        bundles
          .filter((bundle) =>
            isPresentationBundleHidden(bundle, educationSlideVisibility)
          )
          .map((bundle) => bundle.id)
      ),
    [bundles, educationSlideVisibility]
  );

  const previousBundles = usePrevious(bundles);

  useEffect(() => {
    // handle the case where a bundle is removed -- delete all slides from that bundle
    if (isUndefined(previousBundles)) return;

    setSlideIdList((existingSlideIdList) => {
      // filter out slides that are no longer in the bundle or are hidden
      return existingSlideIdList.filter((slideId) => {
        const slideInfo = getSlideInfo(slideId);
        // TS case -- this is derived from the ID, so it should always be defined
        if (!slideInfo) {
          diagnostics.warn('Could not get slide info.', { slideId });
          return false;
        }
        const { bundleId, pageId } = slideInfo;

        const bundle = bundles.find((bundle) => bundle.id === bundleId);

        // bundle is no longer in the list
        if (!bundle) return false;

        if (hiddenBundleIds.has(bundle.id)) return false;

        // if the slide has no page ID, it's a single-page bundle slide, so it should be kept
        if (pageId === NO_PAGE_ID || isNull(pageId)) return true;

        // if the slide has a page ID, it should be kept if it's in the bundle's pages
        return bundle.pages.some((page) => page.id === pageId);
      });
    });
  }, [bundles, hiddenBundleIds, previousBundles]);

  return (
    <ClientPresentationDesignerPaginationContext.Provider
      value={{
        slideIdList,
        setSlideIdList,
        bundles,
        hiddenBundleIds,
        slideIdRegistrationCounts,
      }}
    >
      {children}
    </ClientPresentationDesignerPaginationContext.Provider>
  );
}

/**
 * Register a slide with the presentation, with its
 * order based on the bundle & page IDs and the index within the current page.
 *
 * @returns The slide ID
 */
export function useRegisterSlide(slideInfo: SlideInfo): string {
  const {
    setSlideIdList: setSlideList,
    bundles,
    hiddenBundleIds,
    slideIdRegistrationCounts,
  } = useClientPresentationDesignerPaginationContext();
  const slideId = getSlideId(slideInfo);

  // Bundles flagged `excludeFromOutput` never reach the generated PDF, so they
  // must not consume a page number or a table-of-contents entry. The editor
  // still renders them, which is exactly why the skip lives here rather than at
  // the render site: registration, not rendering, is what allocates the number.
  const isExcludedFromOutput =
    bundles.find((bundle) => bundle.id === slideInfo.bundleId)
      ?.excludeFromOutput ?? false;
  const isHidden = hiddenBundleIds.has(slideInfo.bundleId);

  useEffect(() => {
    if (isExcludedFromOutput || isHidden) return;

    const counts = slideIdRegistrationCounts.current;
    counts.set(slideId, (counts.get(slideId) ?? 0) + 1);
    setSlideList((existingSlideIdList) => {
      return getUpdatedSlideList(slideId, bundles, existingSlideIdList);
    });
    // Drop on unmount (or before re-running with a new slideId) so bundles
    // whose visible slide count shrinks at runtime — e.g.
    // DocumentsReviewedBundle when a card is deleted — don't leak stale
    // pageIndex entries into the navigator. Reference-counted so that
    // overlapping registrations of the same slideId (e.g. BalanceSheetSlide
    // registers at both the parent and the page-0 inner) only get pruned
    // once the *last* consumer unmounts.
    return () => {
      const remaining = (counts.get(slideId) ?? 1) - 1;
      if (remaining > 0) {
        counts.set(slideId, remaining);
        return;
      }
      counts.delete(slideId);
      setSlideList((existingSlideIdList) =>
        existingSlideIdList.filter((id) => id !== slideId)
      );
    };
  }, [
    bundles,
    isExcludedFromOutput,
    isHidden,
    setSlideList,
    slideId,
    slideIdRegistrationCounts,
  ]);

  return slideId;
}

/**
 * Get an updated slide list
 *
 * @param newSlideId The slide ID to add to the list
 * @param bundles List of bundles to sort the slide list by
 * @param existingSlideIdList The existing slide list
 * @returns The updated slide list
 */
export function getUpdatedSlideList(
  newSlideId: string,
  bundles: ClientPresentationV2Bundle[],
  existingSlideIdList: string[]
): string[] {
  let newSlideIdList = existingSlideIdList.slice();
  const slideDetails = getSlideDetails(newSlideId, bundles);

  const existingSlideIndex = getSlideNumber(newSlideId, existingSlideIdList, {
    returnIndex: true,
  });

  if (existingSlideIndex >= 0) {
    newSlideIdList.splice(existingSlideIndex, 1);
  }

  // if we can't get slide details, return the existing list
  if (!slideDetails) return existingSlideIdList;

  newSlideIdList.push(newSlideId);

  const sortCallback = getSortSlideListCallback(bundles);
  return newSlideIdList.sort(sortCallback);
}

/**
 * Get a callback for sorting a slide list
 *
 * @param bundles List of bundles to sort the slide list by
 * @returns Callback for sorting a slide list, intended to be used with `Array.prototype.sort`
 */
export function getSortSlideListCallback(
  bundles: ClientPresentationV2Bundle[]
): (a: string, b: string) => number {
  return (a, b) => {
    const aInfo = getSlideInfo(a);
    const bInfo = getSlideInfo(b);

    if (!aInfo || !bInfo) {
      return 0;
    }

    if (aInfo.bundleId !== bInfo.bundleId) {
      const aBundleIndex = bundles.findIndex(
        (bundle) => bundle.id === aInfo.bundleId
      );
      const bBundleIndex = bundles.findIndex(
        (bundle) => bundle.id === bInfo.bundleId
      );

      return aBundleIndex - bBundleIndex;
    }

    const relevantBundle = bundles.find(
      (bundle) => bundle.id === aInfo.bundleId
    );
    if (!relevantBundle) {
      return 0;
    }

    if (isUndefined(aInfo.pageId) && !isUndefined(bInfo.pageId)) {
      return -1;
    }
    if (!isUndefined(aInfo.pageId) && isUndefined(bInfo.pageId)) {
      return 1;
    }

    if (
      aInfo.pageId !== bInfo.pageId &&
      !isUndefined(aInfo.pageId) &&
      !isUndefined(bInfo.pageId)
    ) {
      const aPageIndex = relevantBundle.pages.findIndex(
        (page) => page.id === aInfo.pageId
      );
      const bPageIndex = relevantBundle.pages.findIndex(
        (page) => page.id === bInfo.pageId
      );

      return aPageIndex - bPageIndex;
    }

    return aInfo.pageIndex - bInfo.pageIndex;
  };
}

const SLIDE_ID_SEPARATOR = '::';
const NO_PAGE_ID = 'NO_PAGE';

/**
 * Get a slide ID from structured data
 */
export function getSlideId(slideInfo: SlideInfo): string {
  return `${slideInfo.bundleId}${SLIDE_ID_SEPARATOR}${slideInfo.pageId || NO_PAGE_ID}${SLIDE_ID_SEPARATOR}${slideInfo.pageIndex}`;
}

/**
 * Get structured data from a slide ID
 */
export function getSlideInfo(slideId: string): SlideInfo | null {
  const [bundleId, pageIdString, pageIndexString = '-1'] =
    slideId.split(SLIDE_ID_SEPARATOR);
  if (!bundleId) return null;

  const pageIndex = parseInt(pageIndexString);

  if (pageIndex < 0 || isNaN(pageIndex)) return null;

  const pageId = pageIdString === NO_PAGE_ID ? null : pageIdString;

  return { bundleId, pageId, pageIndex };
}

// These bundle types should never appear in the ToC
export const NO_TOC_BUNDLE_TYPES: PresentationBundleKind[] = [
  PresentationBundleKind.CoverSlideBundle,
  PresentationBundleKind.TableOfContentsBundle,
  PresentationBundleKind.DividerBundle,
];

/**
 * Get the slide details for a given slide ID
 *
 * @param slideId ID to look up
 * @param bundles List of bundles to look up the slide in
 * @returns Slide details, or null if the slide ID is invalid
 */
export function getSlideDetails(
  slideId: string,
  bundles: ClientPresentationV2Bundle[],
  entityDisplayNameById?: Record<string, string>
): SlideDetails | null {
  const slideInfo = getSlideInfo(slideId);
  if (!slideInfo) return null;

  const bundle = bundles.find((bundle) => bundle.id === slideInfo.bundleId);

  if (!bundle) {
    if (!isEmpty(bundles)) {
      diagnostics.info(
        `No bundles found for slide ${slideId}; this can happen if a bundle is deleted`
      );
    }
    return null;
  }

  const isFirstSlide =
    slideInfo.pageId === first(bundle.pages)?.id ||
    isSinglePageBundle(bundle.type);

  let includeInToC = isFirstSlide && slideInfo.pageIndex === 0;

  if (NO_TOC_BUNDLE_TYPES.includes(bundle.type)) {
    includeInToC = false;
  }

  let title = bundle.displayName;
  if (bundle.type === PresentationBundleKind.EntitySummaryBundle) {
    // Read the live entity name from context (keyed by the bundle's entity id)
    // so renames propagate without a reload — `bundle.displayName` is
    // snapshotted at load by `mapBundleToForm` and would otherwise stay stale.
    const entityId = bundle.entitySummaryConfiguration?.entityId;
    const liveName = entityId ? entityDisplayNameById?.[entityId] : undefined;
    title = liveName || bundle.displayName;
  } else if (
    bundle.type === PresentationBundleKind.DividerBundle &&
    bundle.dividerBundleConfiguration?.dividerTitle
  ) {
    title = `Divider - ${bundle.dividerBundleConfiguration.dividerTitle}`;
  } else if (
    bundle.type === PresentationBundleKind.DocumentsReviewedBundle &&
    bundle.documentsReviewedBundleConfiguration?.displayName
  ) {
    title = bundle.documentsReviewedBundleConfiguration.displayName;
  } else if (
    bundle.type === PresentationBundleKind.TrustOverviewBundle &&
    bundle.trustOverviewBundleConfiguration?.displayName
  ) {
    title = bundle.trustOverviewBundleConfiguration.displayName;
  } else if (
    bundle.type === PresentationBundleKind.BusinessOverviewBundle &&
    bundle.businessOverviewBundleConfiguration?.displayName
  ) {
    title = bundle.businessOverviewBundleConfiguration.displayName;
  } else if (
    bundle.type === PresentationBundleKind.FamilyOverviewBundle &&
    bundle.familyOverviewBundleConfiguration?.displayName
  ) {
    title = bundle.familyOverviewBundleConfiguration.displayName;
  } else if (
    bundle.type === PresentationBundleKind.WillBundle &&
    bundle.willBundleConfiguration?.displayName
  ) {
    title = bundle.willBundleConfiguration.displayName;
  } else if (
    bundle.type === PresentationBundleKind.PowerOfAttorneyBundle &&
    bundle.powerOfAttorneyBundleConfiguration?.displayName
  ) {
    title = bundle.powerOfAttorneyBundleConfiguration.displayName;
  } else if (
    bundle.type === PresentationBundleKind.HealthcareDirectiveBundle &&
    bundle.healthcareDirectiveBundleConfiguration?.displayName
  ) {
    title = bundle.healthcareDirectiveBundleConfiguration.displayName;
  } else if (isLegacySinglePageDiagramBundle(bundle)) {
    // Diagram pages carry their editable title on `diagramPage.pageName`.
    // Read it live so title edits propagate to the ToC without a reload;
    // `bundle.displayName` for a diagram bundle is a static default snapshot
    // (see getBundleDisplayName) that would otherwise stay stale. Mirrors
    // getBundleBaseLabel in the tree view.
    title =
      first(bundle.pages)?.diagramPage?.pageName ||
      bundle.displayName ||
      BUNDLE_TO_DISPLAY_NAME[bundle.type];
  } else if (bundle.type === PresentationBundleKind.AssetDistributionBundle) {
    // Read live from the configuration so renames propagate without a
    // reload, and clearing the title falls back to the kind default
    // ("Asset distribution") rather than to the snapshot stored on
    // `bundle.displayName` at load. The other refresh slides above
    // intentionally fall through to `bundle.displayName` because their
    // `getBundleDisplayName` branch returns `BUNDLE_TO_DISPLAY_NAME`
    // unconditionally, so the snapshot is already the default; asset-
    // distribution's `getBundleDisplayName` returns the user-edited
    // title when set, so the snapshot can go stale.
    title =
      bundle.assetDistributionBundleConfiguration?.displayName ||
      BUNDLE_TO_DISPLAY_NAME[bundle.type];
  } else if (
    bundle.type === PresentationBundleKind.PlanningOpportunitiesBundle &&
    bundle.planningOpportunitiesBundleConfiguration?.displayName
  ) {
    title = bundle.planningOpportunitiesBundleConfiguration.displayName;
  }

  // Per-bundle subtitle (currently only used by the asset-distribution
  // bundle so multiple distribution scenarios in the same deck can be
  // told apart by death-order context).
  let subtitle: string | null = null;
  if (
    bundle.type === PresentationBundleKind.AssetDistributionBundle &&
    bundle.assetDistributionBundleConfiguration?.deathOrderDisplay
  ) {
    subtitle = bundle.assetDistributionBundleConfiguration.deathOrderDisplay;
  }

  return {
    ...slideInfo,
    slideId,
    title,
    includeInToC,
    bundleKind: bundle.type,
    subtitle,
  };
}

/**
 * Get the slide number for a given slide ID
 *
 * @param slideId The slide ID to get the number for
 * @param returnIndex If true, return the index of the slide in the slide list, otherwise return the slide number
 * @returns The slide number
 */
export function useSlideNumber(slideId: string, returnIndex = false): number {
  const { slideIdList: slideList } =
    useClientPresentationDesignerPaginationContext();
  const startFrom = usePresentationConfiguration()?.pageNumberToStartFrom ?? 1;

  return getSlideNumber(slideId, slideList, {
    startFrom,
    returnIndex,
  });
}

/**
 * Get the slide number for a given slide ID
 *
 * When rendering, use `useSlideNumber` instead
 *
 * @param slideId The slide ID to get the number for
 * @param slideIdList The list of slide IDs
 * @param opts Options
 * @returns The slide number
 */
export function getSlideNumber(
  slideId: string,
  slideIdList: string[],
  opts?: {
    startFrom?: number;
    returnIndex?: boolean;
  }
): number {
  const returnIndex = opts?.returnIndex ?? false;
  let startFrom = 0;
  if (!returnIndex) {
    startFrom = opts?.startFrom ?? 1;
  }

  const slideIndex = slideIdList.findIndex((id) => id === slideId);

  if (returnIndex) return slideIndex;

  return slideIndex + startFrom;
}

/**
 * Gets the list of slides and their display details for the ToC
 */
export function useTableOfContents(): SlideDetails[] {
  const { slideIdList, bundles } =
    useClientPresentationDesignerPaginationContext();
  const { contextData } = useClientPresentationDesignerContext();
  // Live entity names (refreshed by `updateContextData` on save) so entity
  // renames show in the table of contents without a page reload.
  const entityDisplayNameById = contextData.entities.reduce<
    Record<string, string>
  >((acc, entity) => {
    if (entity.subtype.displayName) {
      acc[entity.id] = entity.subtype.displayName;
    }
    return acc;
  }, {});
  const slideDetailsList = compact(
    slideIdList.map((slideId) =>
      getSlideDetails(slideId, bundles, entityDisplayNameById)
    )
  );
  return slideDetailsList.filter((slide) => !!slide.includeInToC);
}
